import { mapCmsMovieDataToBoltAppData } from 'utils/mapCmsMovieDataToBoltAppData';
import { getMovieStatus } from 'utils/movieStatus';
import pendingPromise from 'utils/pendingPromise';

const qs = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
export const previewMode = qs.has('cms-preview');

const DATA_DOMAIN =
	__IS_LOCAL__ || __IS_DEMO__
		? (qs.has('prod') ? 'https://www.universalpictures.com' : 'https://hubsite.dev.cmspow.com')
		: (typeof window !== 'undefined' ? location.origin : 'https://www.universalpictures.com');

let previewDataWaitlist = {};
async function waitForPreviewData(name) {
	let promise = pendingPromise();
	previewDataWaitlist[name] ||= [];
	previewDataWaitlist[name].push(promise);
	return promise;
}

if (previewMode) {
	window.addEventListener('message', e => {
		let message = e.data;
		if (message?.type === 'data' && previewDataWaitlist[message.name]) {
			previewDataWaitlist[message.name].forEach(p => p.resolve(message.data));
			previewDataWaitlist[message.name] = [];
		}
	}, false);
}

async function getMovieData(id) {
	// IS_CMS_MOVIE_PAGE is only present on legacy unusable data, so ignore if it is present for now
	if (window.__MOVIE_DATA__ && !window.__MOVIE_DATA__.IS_CMS_MOVIE_PAGE) {
		return window.__MOVIE_DATA__;
	}
	const res = await fetch(new URL(`cms/movies/thundr/${id}.json`, DATA_DOMAIN));
	if (!res.ok) {
		throw new Error(`Data unavailable for ${id}`);
	}
	return await res.json();
}

async function getDataRaw(appData) {
	// TODO: Review this change of splitting usage of id and slug
	const { id } = appData?.meta || {};

	try {
		if (previewMode && window.parent !== window) {
			window.parent.postMessage({ type: 'requestData', name: id }, '*');
			let data = await waitForPreviewData(id);
			if (data !== null) return data;
		}

		const movieData = await getMovieData(id);
		//Movie hasn't been linked yet, so return null and don't use the data
		if (!movieData?.thundr_id) {
			return null;
		}

		// Fetch navigation data for footer legal links
		try {
			const navRes = await fetch(new URL(`cms/sections/navigation.json`, DATA_DOMAIN), { cache: 'no-cache' });
			if (navRes.ok) {
				const navigationData = await navRes.json();
				// Published JSON structure is different: it's already the content (no wrapper)
				// Preview/CMS structure: navigationData.content.footer
				// Published structure: navigationData.footer
				const footerData = navigationData?.content?.footer || navigationData?.footer;
				if (footerData) {
					movieData.footer = movieData.footer || {};
					movieData.footer._navigationFooter = footerData;
				}
				const moviePageFooter = navigationData?.content?.moviePageFooter || navigationData?.moviePageFooter;
				if (moviePageFooter) {
					movieData.footer = movieData.footer || {};
					movieData.footer._navigationFooter = movieData.footer._navigationFooter || {};
					movieData.footer._navigationFooter.moviePageFooter = moviePageFooter;
				}
			}
		} catch (navErr) {
			console.warn('[dynamicData] Could not fetch navigation data:', navErr);
		}

		return movieData;
	} catch (err) {
		console.error('[GET DATA RAW] Error:', err);
	}

	return null;
}

export const OVERRIDE = Symbol('override');
const isObject = (val) => val && typeof val === 'object' && !Array.isArray(val);

/** Recursively removes soft-deleted items (`null` marks an item that no longer exists) */
function stripDeleted(value) {
	if (!isObject(value)) return value;

	return Object.fromEntries(
		Object.entries(value)
			.filter(([, val]) => val !== null)
			.map(([key, val]) => [key, stripDeleted(val)])
	);
}

export function mergeInto(source, target) {
	// 1. Handle non-object inputs (return target if defined, else source)
	if (!isObject(target)) {
		return target !== undefined ? target : stripDeleted(source);
	}

	// 2. Initialize the result object as a shallow copy of the source
	// This ensures we keep properties from source that aren't in target
	const result = Array.isArray(source) ? [...source] : stripDeleted({ ...source });

	// 3. Iterate through target keys to apply overrides
	Object.entries(target)
		.filter(([key, value]) => Object.prototype.hasOwnProperty.call(target, key) && value !== undefined)
		.forEach(([key, value]) => {
			const sourceValue = source ? source[key] : undefined;
			const targetValue = value;
			if (targetValue?.[OVERRIDE]) {
				// If the target value has the OVERRIDE symbol, always replace the source value entirely
				result[key] = targetValue;
				return;
			}

			if (sourceValue === null) {
				// Soft-deleted in the source, so it stays deleted no matter what the target overrides
				delete result[key];
			} else if (isObject(targetValue) && isObject(sourceValue)) {
				// If both are objects, merge them recursively into the new object
				result[key] = mergeInto(sourceValue, targetValue);
			} else if (Array.isArray(targetValue)) {
				// If it's an array, create a fresh copy of the target array
				result[key] = [...targetValue];
			} else if (targetValue === null) {
				// If target value is null, delete the key from the result
				delete result[key];
			} else {
				// Primitives pass through; objects with no source counterpart still need their own deletions applied
				result[key] = stripDeleted(targetValue);
			}
		});

	return result;
}

export async function setAppData(appData) {
	// If Thundr-managed, return appData as-is (don't attempt to fetch or merge CMS data)
	if (appData.options?.thundrManaged) return appData;

	const cmsData = await getDataRaw(appData);
	if (!cmsData) return appData;

	cmsData.status = await getMovieStatus(cmsData);

	const mappedData = mapCmsMovieDataToBoltAppData(cmsData, appData);
	const mergedData = mergeInto(appData, mappedData);

	return mergedData;
}